import type { FunctionComponent } from "react";
import { useCallback, useEffect, useMemo } from "react";
import type { PreloadedQuery } from "react-relay";
import { graphql, useFragment, usePaginationFragment, usePreloadedQuery } from "react-relay";

import { notEmpty } from "shared/lib/utils";

import { Portal } from "scmp-app/components/common/portal";
import { DefaultPageSize } from "scmp-app/components/search/consts";
import type { ArticleResultIdMapEntry } from "scmp-app/components/search/contexts";
import { useSearchContext } from "scmp-app/components/search/contexts";
import { query } from "scmp-app/components/search/search-result-panel";
import { RefineSearchDrawerButton } from "scmp-app/components/search/search-result-panel/refine-search/drawer/button";
import { useInfiniteScrollTriggerReference } from "scmp-app/lib/hooks/infinite-scroll-trigger-reference";
import type { resultListContainerArticleSearchEdge$key } from "scmp-app/queries/__generated__/resultListContainerArticleSearchEdge.graphql";
import type { resultListQuery$key } from "scmp-app/queries/__generated__/resultListQuery.graphql";
import type { searchResultPanelQuery } from "scmp-app/queries/__generated__/searchResultPanelQuery.graphql";

import { useSearchResultSysTracking } from "./hooks";
import {
  Container,
  Count,
  CountRow,
  Header,
  Item,
  ItemContainer,
  NoResultsContainer,
  NoResultsContent,
  NoResultsHeader,
  ResultCount,
  SortBy,
  SortByValue,
  StyledLoading,
  Title,
  TitleRow,
} from "./styles";
import { TopicsCard } from "./topics-card";
import { TopicsCardPortalElementId } from "./topics-card/consts";

type Props = {
  className?: string;
  reference: PreloadedQuery<searchResultPanelQuery>;
};

export const SearchResultList: FunctionComponent<Props> = ({ className, reference }) => {
  const queryReference = usePreloadedQuery(query, reference);
  const {
    data,
    hasNext: hasNextPage,
    isLoadingNext: isLoading,
    loadNext,
  } = usePaginationFragment<searchResultPanelQuery, resultListQuery$key>(
    graphql`
      fragment resultListQuery on Query
      @argumentDefinitions(
        after: { type: "String" }
        articleTypeIds: { type: "[String!]" }
        contentTypes: { type: "[ContentTypeEnum!]" }
        mode: { type: "ArticleSearchAnswerModeEnum" }
        endDate: { type: "Timestamp" }
        first: { type: "Int", defaultValue: 10 }
        orderBy: { type: "ArticlesSearchOrderByInput" }
        paywallTypeIds: { type: "[String!]" }
        query: { type: "String!" }
        sectionIds: { type: "[String!]" }
        startDate: { type: "Timestamp" }
        streamId: { type: "String!" }
        variant: { type: "Int", defaultValue: 0 }
      )
      @refetchable(queryName: "searchArticleResultPaginationQuery") {
        articleSearch(
          filter: {
            articleTypes: $articleTypeIds
            contentTypes: $contentTypes
            endDate: $endDate
            paywallTypeIds: $paywallTypeIds
            query: $query
            sectionIds: $sectionIds
            startDate: $startDate
            variant: $variant
            sse: { enabled: true, streamId: $streamId, mode: $mode }
          }
          orderBy: $orderBy
          first: $first
          after: $after
        ) @connection(key: "articleSearchResultListQuery__articleSearch") {
          resultId
          totalCount
          edges {
            node {
              content {
                entityId
              }
            }
            ...resultListContainerArticleSearchEdge
          }
        }
        topicSearch(first: 10, filter: { query: $query }) {
          edges {
            ...topicsCardTopicSearchEdge
          }
        }
      }
    `,
    queryReference,
  );

  const results = useFragment<resultListContainerArticleSearchEdge$key>(
    graphql`
      fragment resultListContainerArticleSearchEdge on ArticleSearchEdge @relay(plural: true) {
        node {
          id
          content {
            ...searchContentItemContent
          }
        }
      }
    `,
    data.articleSearch?.edges ?? [],
  );

  const { articleResultIdMap, embedMode, refineSearch, setInitialSearchId } = useSearchContext();

  useSearchResultSysTracking();

  const onLoadMore = useCallback(() => loadNext(DefaultPageSize), [loadNext]);
  const { infiniteScrollTriggerReference } = useInfiniteScrollTriggerReference({
    hasNextPage,
    isLoading,
    onLoadMore,
  });

  const noResult = useMemo(
    () => data.articleSearch?.edges?.length === 0 && data.topicSearch?.edges?.length === 0,
    [data.articleSearch?.edges, data.topicSearch?.edges],
  );

  useEffect(() => {
    const edges = data.articleSearch?.edges ?? [];
    const resultId = data.articleSearch?.resultId;
    if (!notEmpty(data) || !notEmpty(articleResultIdMap)) return;

    setInitialSearchId?.(initial => initial ?? resultId ?? "");

    const page = Math.ceil(articleResultIdMap.size / DefaultPageSize) + 1;

    edges.forEach(({ node }, index) => {
      const entityId = node.content?.entityId;
      if (!entityId || articleResultIdMap.has(entityId)) return;

      const position = index - (page - 1) * DefaultPageSize + 1;
      articleResultIdMap.set(entityId, {
        page,
        position,
        resultId,
      } satisfies ArticleResultIdMapEntry);
    });
  }, [articleResultIdMap, data, setInitialSearchId]);

  const sortByLabel =
    refineSearch.orderBy?.field === "PUBLISHED_DATE" ? "Most Recent" : "Relevance";

  const render = () => {
    const totalCount = data.articleSearch?.totalCount ?? 0;
    return (
      <>
        <Portal selector={`#${TopicsCardPortalElementId}`}>
          <TopicsCard reference={data.topicSearch?.edges ?? []} />
        </Portal>

        <Header>
          <TitleRow>
            <Title>All results</Title>
            {!embedMode && <RefineSearchDrawerButton />}
          </TitleRow>

          <CountRow>
            <ResultCount>
              <span>[</span>
              <Count>{totalCount}</Count>
              <span>results</span>
              <span>]</span>
            </ResultCount>
            <SortBy>
              Sort by: <SortByValue>{sortByLabel}</SortByValue>
            </SortBy>
          </CountRow>
        </Header>

        {noResult ? (
          <NoResultsContainer>
            <NoResultsHeader>Sorry! There are no results for your search term.</NoResultsHeader>
            <NoResultsContent>
              Please check if your spelling is correct, or try a different word or phrase.
            </NoResultsContent>
          </NoResultsContainer>
        ) : (
          <ItemContainer>
            {results?.map(
              ({ node }) => node.content && <Item key={node.id} reference={node.content} />,
            )}
          </ItemContainer>
        )}

        {hasNextPage && (
          <StyledLoading
            ref={element => infiniteScrollTriggerReference(isLoading ? null : element)}
          />
        )}
      </>
    );
  };

  return <Container className={className}>{render()}</Container>;
};

SearchResultList.displayName = "SearchResultList";
